<template>
  <div v-if="alternatives.length" class="oos-alternatives">
    <p
      class="oos-alternatives__title text-mobile-paragraph-m-regular text-desktop-paragraph-s-regular"
      v-text="$string('product.oos_alternatives.title')"
    />

    <product-card-compact
      v-for="alternative of alternatives"
      :key="alternative.variant.gid"
      additional-classes="oos-alternatives__card"
      class="oos-alternatives__product"
      :product="alternative.product"
      :variant="alternative.variant"
      image-ratio="image"
      sizes="88px"
      location="OOS Alternatives"
      quick-add-location="OOS Alternatives"
      is-variant
      show-variant
    />
  </div>
</template>

<script>
/**
 * Vue: OOS alternatives (oos-alternatives)
 * -----------------------------------------------------------------------------
 * Shows alternative variants when the customer has selected a sold-out
 * variant on PDP. Configured per-variant via the `custom.oos_alternatives`
 * metafield. Part of Kameleoon AB test ESN-64.
 *
 * @param {Object|Boolean} variant - Canvas formatted, currently selected variant.
 *
 */
import nodesByIdQuery from '~graphql/nodes-by-id-product-card.gql';
import ProductCardCompact from '~async/product-card-compact/product-card-compact';

import { formatProduct } from '~/format/format-product';
import { decodeApiId } from '~/helpers/graphql';

export default {
  name: 'OosAlternatives',

  components: {
    ProductCardCompact,
  },

  provide() {
    return {
      variantsShowQatcOverlay: false,
    };
  },

  props: {
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  data() {
    return {
      alternatives: [],
    };
  },

  watch: {
    /**
     * Watch selected variant to fetch its configured alternatives.
     */
    'variant.gid'() {
      this.fetchAlternatives();
    },
  },

  mounted() {
    this.fetchAlternatives();
  },

  methods: {
    /**
     * Fetch the alternative variants configured on the currently selected
     * variant and drop any that are sold out themselves.
     */
    async fetchAlternatives() {
      const requestedVariantGid = this.variant?.gid;

      this.alternatives = [];

      const ids = this.variant?.oosAlternativeVariantIds;

      if (!ids?.length) {
        return;
      }

      try {
        const response = await cnvs.Query({
          query: nodesByIdQuery,
          variables: {
            country: this.$variable('store.country'),
            ids,
            language: this.$variable('store.language'),
          },
        });

        // Discard the response if the customer has since selected a
        // different variant and this request is no longer the latest one.
        if (this.variant?.gid !== requestedVariantGid) {
          return;
        }

        this.alternatives = response.nodes
          .map((node) => this.formatAlternative(node))
          .filter(Boolean)
          .slice(0, 2);
      } catch (error) {
        cnvs.ReportError('Failed to load OOS alternatives', error);
      }
    },

    /**
     * Format a single alternative node into a product/variant pair, using
     * the same variant image metafield (pim.variant_images) logic as the
     * quick-add overlay.
     * @param {Object} node - Raw `ProductVariant` node from the GraphQL response.
     * @returns {Object|Boolean}
     */
    formatAlternative(node) {
      if (!node?.product) {
        return false;
      }

      const formattedProduct = formatProduct(node.product, {
        variantId: decodeApiId(node.id),
      });
      const variant = formattedProduct.selectedOrFirstAvailableVariant;

      if (!variant?.available) {
        return false;
      }

      if (variant.media?.length) {
        variant.localizedImage = variant.media[0].image;
      }

      return { product: formattedProduct, variant };
    },
  },
};
</script>

<style lang="scss">
@import './oos-alternatives';
</style>
